import { Component, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import { FlatTreeControl, NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeNestedDataSource } from '@angular/material/tree';
import { UtilsService } from '@core/services/utils.service';

export interface Node {
  name: string;
  value?: any;
  children?: Node[];
  expandable?: boolean;
  level?: number;
  parent: any;
}

@Component({
  selector: 'app-data-tree',
  templateUrl: './data-tree.component.html',
  styleUrls: ['./data-tree.component.scss']
})
export class DataTreeComponent {
  _data: any
  @Input() 
  set data(json) {
    if (!json) return;
    const transformData = this.transformData(json);
    this.dataSource.data = this._data = transformData;
    this.expandDefaults()
    if (!this.flatten) {
      this.expandAll()
    }
  }
  @Input() targetName: string
  @Input() flatten: boolean = false
  @Input() defaultExpanded: string[] = []
  @ViewChildren('headerNode') headerNodes: QueryList<ElementRef<HTMLDivElement>>;

  treeControl: FlatTreeControl<Node> | NestedTreeControl<Node>
  dataSource
  treeFlattener

  constructor(private utils: UtilsService) { }

  get topLevelNodes() {
    return this._data
  }

  ngOnInit(): void {
    if (this.flatten) {
      this.treeControl = new FlatTreeControl<Node>(
        node => node.level, node => node.expandable);
      this.treeFlattener = new MatTreeFlattener(
          this.flatTransformer, node => node.level, node => node.expandable, node => node.children);
    
      this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    } else {
      this.treeControl = new NestedTreeControl<Node>(node => node.children);
      this.dataSource = new MatTreeNestedDataSource<Node>();
    }
  }

  hasChild = (_: number, node: Node) => node.expandable || (!!node.children && node.children.length);

  private transformData(config: any): Node[] {
    const transform = (obj: any, name = null, parent = null): Node => {
      if (!name) {
        let newName;
        if (this.targetName) {
          if (Array.isArray(obj)) {
            newName = obj[0][this.targetName];
          } else {
            newName = obj[this.targetName];
          }
        } else {
          newName = Object.entries(obj)[0][1]
        }
        name = newName ?? (Array.isArray(obj) ? "<List>" : "<Section>");
      }
      
      const config = {
        name, parent,
        children: typeof obj === 'object' && obj !== null 
          ? Object.keys(obj).map(key => {
            const newName = !isNaN(Number(key)) ? `${Number(key) + 1} - ${name}` : key
            return transform(obj[key], newName, true)
          })
          : undefined,
        value: (typeof obj === 'object' && !Array.isArray(obj) && obj !== null) ? '' : obj,
      }
      return config
    };

    if (this.flatten) {
      config = this.flattenData([config]);
    }
    if (Array.isArray(config)) {
      return config.map(node => transform(node));
    }
    return [transform(config)];
  }

  private flatTransformer = (node: Node, level: number) => {
    return {
      expandable: Boolean(node.children?.length),
      name: node.name,
      level: level,
      value: node.value,
      parent: node.parent
    };
  }

  filterNodes(value: string): void {
    if (!value) {
      this.dataSource.data = this._data;
      return;
    }
    const filterData = (nodes: Node[]): Node[] => nodes
      .map(node => ({
        ...node,
        children: node.children ? filterData(node.children) : undefined
      }))
      .filter(node => this.filterNode(node, value));
    this.dataSource.data = filterData(this._data);
    this.expandAll()
  }

  private filterNode(node: Node, searchTerm: string): boolean {
    if (node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (node.value && node.value.toString().toLowerCase().includes(searchTerm.toLowerCase()))) {
      return true;
    }
    if (node.children) {
      return node.children.some(child => this.filterNode(child, searchTerm));
    }
    return false;
  }
  
  private flattenData(objs, allObjs: { [k: string]: any[] } = {}) {
    const nestedObjs = [];
    let objName;

    objs.forEach(obj => {
      const ret = Object.entries(obj).reduce((final, [k, v]) => {
        if (k === this.targetName) {
          objName = v;
        }
        if (this.utils.isObjOrArray(v)) {
          if (Array.isArray(v)) nestedObjs.push(...v);
          else nestedObjs.push(v);
        } else return {...final, [k]: v} 
        return final
      }, {})
      allObjs[objName] = [...(allObjs[objName] ?? []), ret];
    })

    if (nestedObjs.length) {
      return this.flattenData(nestedObjs, allObjs);
    }
    
    return Object.values(allObjs).map(nodes => (
      nodes.length > 1 ? nodes : nodes[0])
    )
  }

  expandNode(node: Node) {
    const dataNode = this.treeControl.dataNodes.find(({ name }) => {
      return name === node.name;
    })
    if (!dataNode) return
    this.treeControl.expand(dataNode);
    this.treeControl.expandDescendants(dataNode);
  }

  expandAll() {
    if (this.flatten) {
      this.treeControl.expandAll();
    } else {
      this.dataSource.data.forEach(node => {
        this.treeControl.expandDescendants(node);
      });
    }
  }

  collapseAll() {
    if (this.flatten) {
      this.treeControl.collapseAll()
    } else {
      this.dataSource.data.forEach(node => {
        this.treeControl.collapseDescendants(node);
      });
    }
  }

  _isArray(value: any) { return Array.isArray(value) }

  private expandDefaults() {
    const nodes = this.flatten ? this.treeControl.dataNodes : this.dataSource.data

    for (const node of nodes) {
      if (this.defaultExpanded.includes(node.name)) {
        this.treeControl.expand(node)
        this.treeControl.expandDescendants(node)
        return
      }
    }
  }

  handleNodeClick(node: Node) {
    if (!this.treeControl.isExpanded(node)) {
      this.treeControl.expand(node)
      this.treeControl.expandDescendants(node)
      this.scrollToNode(node)
    } else {
      this.treeControl.collapse(node)
    }
  }

  scrollToNode(node: Node) {
    this.headerNodes.find(({ nativeElement }) => {
      return nativeElement.innerText.includes(node.name)
    })?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}